import { useAppTheme } from "@hooks/useAppTheme";
import type { FlashcardEntry } from "@memorang/types/src/session/new-session";
import { Box, MarkdownRenderer } from "@memorang/ui";
import CustomTabs from "components/CustomTabs";
import { Image } from "expo-image";
import { useRef, useState } from "react";
import { Pressable, ScrollView, useWindowDimensions } from "react-native";
import { Icon, Text } from "react-native-paper";

interface Props {
	cards: FlashcardEntry[];
	itemId: number;
	onAllViewed?: () => void;
}

type Section = {
	title: string;
	content: string;
	icon: string;
};

const SECTION_META: Record<string, { title: string; icon: string }> = {
	ANALYSIS: { title: "Analysis", icon: "text-box-search-outline" },
	"KEY TAKEAWAY": { title: "Key Takeaway", icon: "key-outline" },
	QUESTIONS: { title: "Questions", icon: "comment-question-outline" },
};

const parseSections = (content: string): Section[] => {
	const matches = [
		...content.matchAll(/^(ANALYSIS|QUESTIONS|KEY TAKEAWAY)\s*$/gm),
	];

	if (!matches.length) {
		return [
			{
				title: "Analysis",
				icon: SECTION_META.ANALYSIS.icon,
				content,
			},
		];
	}

	return matches.map((match, index) => {
		const key = match[1] ?? "ANALYSIS";
		const start = (match.index ?? 0) + match[0].length;
		const end = matches[index + 1]?.index ?? content.length;
		const meta = SECTION_META[key] ?? SECTION_META.ANALYSIS;

		return {
			title: meta.title,
			icon: meta.icon,
			content: content.slice(start, end).trim(),
		};
	});
};

const AnalysisPanel = ({ card }: { card: FlashcardEntry }) => {
	const theme = useAppTheme();
	const { width } = useWindowDimensions();
	const isWide = width >= 900;
	const imageHeight = isWide ? 320 : 260;
	const sections = parseSections(card.back);
	const [expandedSections, setExpandedSections] = useState(
		() => new Set<string>(),
	);
	const [viewedSections, setViewedSections] = useState(() => new Set<string>());

	const toggleSection = (title: string) => {
		setExpandedSections((current) => {
			const next = new Set(current);
			if (next.has(title)) {
				next.delete(title);
			} else {
				next.add(title);
			}
			return next;
		});
		setViewedSections((current) => new Set(current).add(title));
	};

	return (
		<ScrollView showsVerticalScrollIndicator={false}>
			<Box
				paddingTop={20}
				gap={20}
				flexDirection={isWide ? "row" : "column"}
				alignItems={isWide ? "flex-start" : "stretch"}
			>
				{card.front.image ? (
					<Box
						borderWidth={1}
						borderColor={theme.colors.outlineVariant}
						backgroundColor={theme.colors.surface}
						padding={14}
						gap={12}
						style={{
							width: isWide ? 340 : "100%",
							flexShrink: 0,
						}}
					>
						<Box
							overflow="hidden"
							backgroundColor={theme.colors.surfaceVariant}
						>
							<Image
								source={{ uri: card.front.image }}
								contentFit="cover"
								contentPosition="center"
								style={{ width: "100%", height: imageHeight }}
							/>
						</Box>
						<Text
							variant="titleMedium"
							style={{
								color: theme.colors.onSurface,
								fontWeight: "800",
								textAlign: "center",
							}}
						>
							{card.front.text}
						</Text>
					</Box>
				) : null}

				<Box flex={1} gap={12} width={isWide ? undefined : "100%"}>
					{sections.map((section) => {
						const isExpanded = expandedSections.has(section.title);
						const isViewed = viewedSections.has(section.title);

						return (
							<Box
								key={section.title}
								borderWidth={1}
								borderColor={theme.colors.outlineVariant}
								backgroundColor={theme.colors.surface}
							>
								<Pressable
									onPress={() => toggleSection(section.title)}
									accessibilityRole="button"
									accessibilityState={{ expanded: isExpanded }}
									style={{
										flexDirection: "row",
										alignItems: "center",
										gap: 10,
										paddingHorizontal: 14,
										paddingVertical: 12,
									}}
								>
									<Box
										width={32}
										height={32}
										alignItems="center"
										justifyContent="center"
										backgroundColor={theme.colors.primaryContainer}
									>
										<Icon
											source={section.icon}
											size={18}
											color={theme.colors.primary}
										/>
									</Box>
									<Text
										variant="titleSmall"
										style={{
											flex: 1,
											color: theme.colors.onSurface,
											fontWeight: "800",
										}}
									>
										{section.title}
									</Text>
									{isViewed ? null : (
										<Box
											width={8}
											height={8}
											borderRadius={4}
											backgroundColor={theme.colors.error}
										/>
									)}
									<Icon
										source={isExpanded ? "chevron-up" : "chevron-down"}
										size={22}
										color={theme.colors.onSurfaceVariant}
									/>
								</Pressable>
								{isExpanded ? (
									<Box
										padding={16}
										borderWidth={1}
										borderColor={theme.colors.outlineVariant}
										style={{
											borderBottomWidth: 0,
											borderLeftWidth: 0,
											borderRightWidth: 0,
										}}
									>
										<MarkdownRenderer
											text={section.content}
											variant="explanation"
											fontSize={14}
											customStyles={{
												paragraph: { fontSize: 14, lineHeight: 21 },
												text: { fontSize: 14, lineHeight: 21 },
												bullet_list: { fontSize: 14, lineHeight: 21 },
												list_item: { fontSize: 14, lineHeight: 21 },
											}}
										/>
									</Box>
								) : null}
							</Box>
						);
					})}
				</Box>
			</Box>
		</ScrollView>
	);
};

const AnalysisTabs = ({ cards, onAllViewed }: Props) => {
	const [visitedIndexes, setVisitedIndexes] = useState(() => new Set([0]));
	const hasMarkedAllViewed = useRef(false);

	const markVisited = (index: number) => {
		setVisitedIndexes((current) => {
			if (current.has(index)) {
				return current;
			}
			const next = new Set(current);
			next.add(index);
			if (next.size === cards.length && !hasMarkedAllViewed.current) {
				hasMarkedAllViewed.current = true;
				onAllViewed?.();
			}
			return next;
		});
	};

	return (
		<CustomTabs
			mode="fixed"
			onChangeIndex={markVisited}
			tabs={cards.map((card, index) => ({
				label: card.front.text,
				badge: visitedIndexes.has(index) ? undefined : true,
				content: <AnalysisPanel card={card} />,
			}))}
		/>
	);
};

export default AnalysisTabs;
